import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import {
  CreateForstaProjectByDatasetCommand,
  CreateForstaProjectCommand,
  ForstaProjectsClient,
  ForstaProjectStatuses,
  IForstaProjectsClient,
} from "app/ApiClient";
import { initialForstaProjectState } from "slices/forstaProject/forstaProjectState";
import { LoadingState } from "types";

const forstaProjectClient: IForstaProjectsClient = new ForstaProjectsClient(process.env.REACT_APP_API_URL);

export const exportForstaProject = createAsyncThunk(
  "forstaProject/export",
  async (command: Omit<CreateForstaProjectCommand, "batteries">, { rejectWithValue, getState }) => {
    try {
      const {
        projectsData: { pageContext },
      } = getState() as RootState;
      const batteries = pageContext.filter?.batteries ?? [];
      const response = await forstaProjectClient.create({ ...command, batteries });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const exportForstaProjectByDataset = createAsyncThunk(
  "forstaProject/exportByDataset",
  async (command: CreateForstaProjectByDatasetCommand, { rejectWithValue }) => {
    try {
      const response = await forstaProjectClient.createByDataset({ ...command });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getLatestForstaProject = createAsyncThunk(
  "forstaProject/getLatestForstaProject",
  async (
    {
      projectId,
      versionId,
      forstaProjectId,
      modificationId,
      stackId,
    }: {
      projectId: number;
      versionId: string;
      forstaProjectId : string;
      modificationId: Nullable<number>;
      stackId: Nullable<number>;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await forstaProjectClient.getLatestByProjectId(projectId, versionId, forstaProjectId, modificationId, stackId);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getForstaProjectStatus = createAsyncThunk(
  "forstaProject/getForstaProjectStatus",
  async (id: number, { rejectWithValue, signal }) => {
    try {
      const response = await forstaProjectClient.getStatusById(id);
      if (signal.aborted) {
        return;
      }
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateForstaProjectStatus = createAsyncThunk(
  "forstaProject/updateForstaProjectStatus",
  async ({ id, status, forstaProjectId }: { id: number; status: ForstaProjectStatuses; forstaProjectId: string; }, { rejectWithValue }) => {
    try {
      const response = await forstaProjectClient.updateStatus(id, status, forstaProjectId);     
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const forstaProjectSlice = createSlice({
  name: "forstaProject",
  initialState: initialForstaProjectState,
  reducers: {
    resetProjectExport: () => initialForstaProjectState,
    setForstaExportDialogOpenedState: (state, { payload }: PayloadAction<boolean>) => {
      state.isForstaExportDialogOpened = payload;
      state.exportLoading = LoadingState.idle;
    },
  },
  extraReducers: (builder) => {
    // exportForstaProject
    builder.addCase(exportForstaProject.pending, (state) => {
      state.exportLoading = LoadingState.pending;
      state.projectStatus = null;
      state.exportErrorMessage = undefined;      
    });
    builder.addCase(exportForstaProject.fulfilled, (state, { payload }) => {
      state.exportLoading = LoadingState.succeed;
      state.project = payload;
    });
    builder.addCase(exportForstaProject.rejected, (state, { error: { message } }) => {
      state.exportLoading = LoadingState.failed;
      state.exportErrorMessage = message;
    });

    // getLatestForstaProject
    builder.addCase(getLatestForstaProject.pending, (state) => {
      state.getLatestLoading = LoadingState.pending;
      state.projectStatus = null;
      state.project = null;
    });
    builder.addCase(getLatestForstaProject.fulfilled, (state, { payload }) => {
      state.getLatestLoading = LoadingState.succeed;
      state.project = payload;
    });
    builder.addCase(getLatestForstaProject.rejected, (state, { error: { message } }) => {
      state.getLatestLoading = LoadingState.failed;
      state.errorMessage = message;
    });

    // getForstaProjectStatus
    builder.addCase(getForstaProjectStatus.pending, (state) => {
      state.getStatusLoading = LoadingState.pending;
    });
    builder.addCase(getForstaProjectStatus.fulfilled, (state, { payload }) => {
      state.getStatusLoading = LoadingState.succeed;
      state.projectStatus = payload;
    });
    builder.addCase(getForstaProjectStatus.rejected, (state, { error: { message } }) => {
      state.getStatusLoading = LoadingState.failed;
      state.errorMessage = message;
    });

    // updateForstaProjectStatus
    builder.addCase(updateForstaProjectStatus.pending, (state) => {
      state.updateLoading = LoadingState.pending;
    });
    builder.addCase(updateForstaProjectStatus.fulfilled, (state, { payload }) => {      
      state.updateLoading = LoadingState.succeed;
      state.projectStatus = payload;
      state.getStatusLoading = LoadingState.idle;
      if (state.project) {
        state.project = { ...state.project, status: payload.status };
      }      
    });
    builder.addCase(updateForstaProjectStatus.rejected, (state, { error: { message } }) => {
      state.updateLoading = LoadingState.failed;
      state.errorMessage = message;
    });
    // exportForstaProjectByDataset
    builder.addCase(exportForstaProjectByDataset.pending, (state) => {
      state.exportLoading = LoadingState.pending;
      state.projectStatus = null;
      state.exportErrorMessage = undefined;      
    });
    builder.addCase(exportForstaProjectByDataset.fulfilled, (state, { payload }) => {
      state.exportLoading = LoadingState.succeed;
      state.project = payload;
    });
    builder.addCase(exportForstaProjectByDataset.rejected, (state, { error: { message } }) => {
      state.exportLoading = LoadingState.failed;
      state.exportErrorMessage = message;
    });
  },
});
export const { resetProjectExport, setForstaExportDialogOpenedState } = forstaProjectSlice.actions;

export default forstaProjectSlice.reducer;
